import { useTranslator } from "@eo-locale/react";

import {
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import type { ChangeEvent } from "react";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface TosQuestionProps {
  question: Partial<Question>;
  isAsterixRequired?: boolean;
}

export const validate = async (answer: "0" | "1"): Promise<string[]> => {
  return answer === "1" ? [] : ["accepted"];
};

const TosQuestion = ({
  question,
  isAsterixRequired = true
}: TosQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || 0;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.checked ? "1" : ""
      })
    );
  };

  return (
    <FormControl
      my={4}
      isInvalid={error.length > 0}
      isRequired={question.required && isAsterixRequired}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Checkbox
        id={`${question.id}`}
        fontWeight="semibold"
        onChange={handleChange}
        data-testid="tosQuestion-checkbox"
        isChecked={answer === "1"}
        isInvalid={error.length > 0}
        isDisabled={status === "saving"}
        size={{ base: "lg", lg: "md" }}
        sx={{
          "> .chakra-checkbox__control": { alignSelf: "baseline", mt: "4px" }
        }}
      >
        <FormLabel m={0} htmlFor={`${question.id}`}>
          <HTMLWrapper html={question.question} />
          {question.required && (
            <VisuallyHidden>{`* ${translate(
              "common.required"
            )}`}</VisuallyHidden>
          )}
        </FormLabel>
      </Checkbox>
      {error.length > 0 && (
        <FormErrorMessage data-testid="tosQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default TosQuestion;
